import { fechaLarga, sumarDias } from "./agenda.ts";

/**
 * Feriados nacionales de Ecuador (Código del Trabajo, art. 65, con la reforma de diciembre de 2016) y los ajustes de cada clínica.
 * El calendario de cada año lo confirma el Gobierno y a veces decreta días «puente» (p. ej. el 2 de enero de 2026): por eso aquí se calcula con la ley
 * y cada clínica puede quitar un día o agregar los suyos (feriado local, puente, cierre). Comprobado con el calendario oficial de 2026.
 */
export interface Feriado { fecha: string; nombre: string; tipo: "nacional" | "propio"; /** p. ej. «Se traslada desde el domingo 24 de mayo» */ nota?: string }
export interface AjusteFeriados { extras: { fecha: string; nombre: string }[]; quitados: string[] }
export const AJUSTE_FERIADOS_VACIO: AjusteFeriados = { extras: [], quitados: [] };

const p2 = (n: number) => String(n).padStart(2, "0");
const diaSemana = (f: string) => new Date(f + "T12:00:00Z").getUTCDay(); // 0 = domingo
export const fechaIsoValida = (f: unknown): f is string => typeof f === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f) && !Number.isNaN(Date.parse(f + "T12:00:00Z")) && new Date(f + "T12:00:00Z").toISOString().slice(0, 10) === f;

/** Domingo de Pascua (calendario gregoriano). */
export function pascua(anio: number): string {
  const a = anio % 19, b = Math.floor(anio / 100), c = anio % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31), dia = ((h + l - 7 * m + 114) % 31) + 1;
  return `${anio}-${p2(mes)}-${p2(dia)}`;
}

/** Feriados movibles: se trasladan según el día de la semana en que caen. El 1 de enero, el 25 de diciembre y el martes de Carnaval no se mueven. */
const MOVIBLES: readonly (readonly [string, string])[] = [
  ["05-01", "Día del Trabajo"], ["05-24", "Batalla de Pichincha"], ["08-10", "Primer Grito de Independencia"],
  ["10-09", "Independencia de Guayaquil"], ["11-02", "Día de los Difuntos"], ["11-03", "Independencia de Cuenca"],
];

/**
 * Los feriados nacionales de un año con sus traslados: martes → el lunes anterior; miércoles → el viernes siguiente; jueves → el viernes;
 * sábado → el viernes anterior; domingo → el lunes siguiente. Si dos feriados seguidos chocan: martes y miércoles (el miércoles pasa al lunes y el martes se queda),
 * miércoles y jueves (el jueves se queda), jueves y viernes (el jueves se queda), lunes y martes (el martes se queda), viernes y sábado (el sábado pasa al jueves) y domingo y lunes (el domingo pasa al martes).
 */
export function feriadosNacionales(anio: number): Feriado[] {
  const pas = pascua(anio);
  const fijos: Feriado[] = [
    { fecha: `${anio}-01-01`, nombre: "Año Nuevo", tipo: "nacional" },
    { fecha: sumarDias(pas, -48), nombre: "Lunes de Carnaval", tipo: "nacional" },
    { fecha: sumarDias(pas, -47), nombre: "Martes de Carnaval", tipo: "nacional" },
    { fecha: sumarDias(pas, -2), nombre: "Viernes Santo", tipo: "nacional" },
    { fecha: `${anio}-12-25`, nombre: "Navidad", tipo: "nacional" },
  ];
  const originales = new Set(MOVIBLES.map(([md]) => `${anio}-${md}`));
  const ocupados = new Set([...fijos.map((x) => x.fecha), ...originales]);
  const movidos = MOVIBLES.map(([md, nombre]): Feriado => {
    const fecha = `${anio}-${md}`, d = diaSemana(fecha);
    let destino = fecha;
    if (d === 2) destino = ocupados.has(sumarDias(fecha, -1)) || originales.has(sumarDias(fecha, 1)) ? fecha : sumarDias(fecha, -1);
    else if (d === 3) destino = originales.has(sumarDias(fecha, -1)) ? sumarDias(fecha, -2) : sumarDias(fecha, 2);
    else if (d === 4) destino = originales.has(sumarDias(fecha, -1)) || originales.has(sumarDias(fecha, 1)) ? fecha : sumarDias(fecha, 1);
    else if (d === 6) destino = ocupados.has(sumarDias(fecha, -1)) ? sumarDias(fecha, -2) : sumarDias(fecha, -1);
    else if (d === 0) destino = ocupados.has(sumarDias(fecha, 1)) ? sumarDias(fecha, 2) : sumarDias(fecha, 1);
    return destino === fecha ? { fecha, nombre, tipo: "nacional" } : { fecha: destino, nombre, tipo: "nacional", nota: `Se traslada desde el ${fechaLarga(fecha)}` };
  });
  return [...fijos, ...movidos].sort((a, b) => a.fecha.localeCompare(b.fecha));
}

/** Ajustes guardados por la clínica → solo lo válido (fechas reales, nombres cortos, sin repetir). */
export function leerAjusteFeriados(v: unknown): AjusteFeriados {
  const o = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const extras: AjusteFeriados["extras"] = [];
  for (const x of Array.isArray(o.extras) ? o.extras.slice(0, 300) : []) {
    const e = x && typeof x === "object" ? (x as Record<string, unknown>) : {};
    const nombre = typeof e.nombre === "string" ? e.nombre.trim().slice(0, 60) : "";
    if (fechaIsoValida(e.fecha) && nombre && !extras.some((y) => y.fecha === e.fecha)) extras.push({ fecha: e.fecha, nombre });
  }
  const quitados = [...new Set((Array.isArray(o.quitados) ? o.quitados.slice(0, 400) : []).filter(fechaIsoValida))];
  return { extras: extras.sort((a, b) => a.fecha.localeCompare(b.fecha)), quitados: quitados.sort() };
}

/** Los feriados de la clínica entre dos fechas (ambas incluidas): los nacionales menos los que quitó, más los propios que agregó. */
export function feriadosEntre(desde: string, hasta: string, ajuste: AjusteFeriados = AJUSTE_FERIADOS_VACIO): Feriado[] {
  const salida: Feriado[] = [];
  const a1 = Number(desde.slice(0, 4)), a2 = Number(hasta.slice(0, 4));
  for (let anio = a1; anio <= a2 && anio - a1 < 10; anio++)
    for (const f of feriadosNacionales(anio)) if (f.fecha >= desde && f.fecha <= hasta && !ajuste.quitados.includes(f.fecha)) salida.push(f);
  for (const e of ajuste.extras) if (e.fecha >= desde && e.fecha <= hasta && !salida.some((f) => f.fecha === e.fecha)) salida.push({ fecha: e.fecha, nombre: e.nombre, tipo: "propio" });
  return salida.sort((a, b) => a.fecha.localeCompare(b.fecha));
}
